import React, {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "../../../api/apiClient";
import config from "../../../config";
import PageHeader from "../../../app/layout/PageHeader";
import { useStaticData } from "../../../context/StaticDataContext";
import AddressCard from "../manage-party/components/AddressCard";
import ContactCard from "../manage-party/components/ContactCard";
import { ContactMethod, PartyAddress } from "../../types/types";
import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/forms.css";
import "../../../css/tables.css";
import "./CommunityDirectoryImportPage.css";

interface ImportBatch {
  importBatchId: number;
  originalFileName: string;
  status: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  stagedRows: number;
  skippedRows: number;
  organisationCount: number;
  addressCount: number;
  contactCount: number;
  serviceCount: number;
  scheduleCount: number;
  createdByUserId?: string;
  createdDate: string;
  errorMessage?: string;
}

interface ImportResult {
  importBatchId: number;
  status: string;
  alreadyCompleted: boolean;
  createdOrganisations: number;
  matchedOrganisations: number;
  createdAddresses: number;
  reusedAddresses: number;
  createdContacts: number;
  reusedContacts: number;
  createdServices: number;
  matchedServices: number;
  createdSchedules: number;
  reusedSchedules: number;
}

interface ImportRow {
  importBatchRowId: number;
  sheetName: string;
  rowNumber: number;
  entityType: string;
  externalKey?: string;
  rawDataJson: string;
  validationStatus: string;
  validationErrors: string[];
  validationWarnings: string[];
}

interface Organisation {
  organisationStagingId: number;
  importBatchId: number;
  importBatchRowId: number;
  organisationKey: string;
  organisationName: string;
  legalName?: string;
  description?: string;
  charityNumber?: string;
  companyNumber?: string;
  website?: string;
  sourceUrl: string;
  confidence?: string;
  confidenceReason?: string;
  matchedPartyId?: number;
  matchedTenantId?: number;
  importedPartyId?: number;
  importedTenantId?: number;
  reviewStatus: string;
  importStatus: string;
  reviewNotes?: string;
  createdDate: string;
  serviceCount: number;
  addresses: Array<{
    organisationAddressStagingId: number;
    addressKey: string;
    addressName: string;
    addressType?: string;
    addressLine1?: string;
    addressLine2?: string;
    townCity?: string;
    county?: string;
    postcode: string;
    country?: string;
    isPrimary: boolean;
    isActivityLocation: boolean;
    sourceUrl?: string;
  }>;
  contacts: Array<{
    organisationContactStagingId: number;
    contactType: string;
    contactValue: string;
    contactLabel?: string;
    isPrimary: boolean;
    isPublic: boolean;
  }>;
}

interface Service {
  serviceStagingId: number;
  importBatchRowId: number;
  organisationStagingId: number;
  organisationName: string;
  serviceKey: string;
  serviceName: string;
  description?: string;
  serviceCategoryName?: string;
  addressName?: string;
  audience?: string;
  minimumAge?: number;
  maximumAge?: number;
  costDescription?: string;
  bookingRequired: boolean;
  referralRequired: boolean;
  publicRegistrationAvailable: boolean;
  sourceUrl: string;
  matchedServiceId?: number;
  importedServiceId?: number;
  proposedServiceCategoryId?: number;
  selectedPartyAddressId?: number;
  reviewStatus: string;
  importStatus: string;
  reviewNotes?: string;
  scheduleCount: number;
}

interface Schedule {
  serviceScheduleStagingId: number;
  importBatchRowId: number;
  serviceStagingId: number;
  organisationName: string;
  serviceName: string;
  scheduleKey: string;
  rawScheduleText: string;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  frequencyType?: string;
  intervalValue?: number;
  weekOfMonth?: number;
  startDate?: string;
  endDate?: string;
  termTimeOnly: boolean;
  parseStatus: string;
  sourceUrl?: string;
  reviewStatus: string;
  importStatus: string;
  reviewNotes?: string;
}

type ReviewRecord = Organisation | Service | Schedule;
type Tab = "upload" | "batches" | "review" | "errors";

const isScheduleRecord = (record: ReviewRecord): record is Schedule =>
  "serviceScheduleStagingId" in record;
const isServiceRecord = (record: ReviewRecord): record is Service =>
  !isScheduleRecord(record) && "serviceStagingId" in record;

const tabs: Array<{ key: Tab; label: string }> = [
  { key: "upload", label: "Upload Spreadsheet" },
  { key: "batches", label: "Import Batches" },
  { key: "review", label: "Review Import" },
  { key: "errors", label: "Import Errors" },
];

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";
const yesNo = (value: boolean) => (value ? "Yes" : "No");

export default function CommunityDirectoryImportPage() {
  const location = useLocation();
  const requestedTab = location.pathname.split("/").pop() || "upload";
  const activeTab: Tab = ["organisations", "services", "schedules"].includes(
    requestedTab,
  )
    ? "review"
    : tabs.some((item) => item.key === requestedTab)
      ? (requestedTab as Tab)
      : "upload";
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [reviewErrors, setReviewErrors] = useState<ImportRow[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportBatch | null>(null);
  const [selected, setSelected] = useState<ReviewRecord | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importingRecordKey, setImportingRecordKey] = useState<string | null>(
    null,
  );
  const [confirmingRecordKey, setConfirmingRecordKey] = useState<string | null>(
    null,
  );
  const reviewBatches = useMemo(
    () => batches.filter((batch) => batch.status !== "Cancelled"),
    [batches],
  );

  const loadBatches = useCallback(async () => {
    const result = await api<ImportBatch[]>(
      "/api/community-directory-import/batches",
    );
    setBatches(result);
    setSelectedBatchId((current) => current || result[0]?.importBatchId || 0);
  }, []);

  const loadReviewData = useCallback(async () => {
    if (activeTab !== "review") return;
    const [organisationValues, serviceValues, scheduleValues] =
      await Promise.all([
        api<Organisation[]>("/api/community-directory-import/organisations"),
        api<Service[]>("/api/community-directory-import/services"),
        api<Schedule[]>("/api/community-directory-import/schedules"),
      ]);
    setOrganisations(organisationValues);
    setServices(serviceValues);
    setSchedules(scheduleValues);
  }, [activeTab]);

  useEffect(() => {
    if (["batches", "review", "errors"].includes(activeTab)) loadBatches();
    if (activeTab === "review") loadReviewData();
  }, [activeTab, loadBatches, loadReviewData]);

  useEffect(() => {
    if (activeTab !== "review") return;
    setSelectedBatchId((current) =>
      reviewBatches.some((batch) => batch.importBatchId === current)
        ? current
        : reviewBatches[0]?.importBatchId || 0,
    );
  }, [activeTab, reviewBatches]);

  useEffect(() => {
    if (activeTab !== "errors" || !selectedBatchId) return;
    api<ImportRow[]>(
      `/api/community-directory-import/batches/${selectedBatchId}/errors`,
    ).then(setRows);
  }, [activeTab, selectedBatchId]);

  useEffect(() => {
    if (
      activeTab !== "review" ||
      !selectedBatchId ||
      !reviewBatches.some((batch) => batch.importBatchId === selectedBatchId)
    )
      return;
    api<ImportRow[]>(
      `/api/community-directory-import/batches/${selectedBatchId}/errors`,
    ).then(setReviewErrors);
  }, [activeTab, reviewBatches, selectedBatchId]);

  const headerActions = useMemo(
    () => (
      <a
        className="action-button secondary"
        href={`${config.API_BASE_URL.replace(/\/+$/, "")}/api/community-directory-import/template`}
      >
        Download template
      </a>
    ),
    [],
  );

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file || isUploading) return;
    const body = new FormData();
    body.append("file", file);
    setIsUploading(true);
    try {
      const result = await api<ImportBatch>(
        "/api/community-directory-import/upload",
        {
          method: "POST",
          body,
          notifySuccess: "Spreadsheet validated and staged.",
        },
      );
      setSummary(result);
      setSelectedBatchId(result.importBatchId);
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  }

  async function importBatch(batchId: number) {
    if (
      !window.confirm(
        "Import all approved organisations, services and schedules in this batch? This creates live directory records.",
      )
    )
      return;

    setIsImporting(true);
    try {
      await api<ImportResult>(
        `/api/community-directory-import/batches/${batchId}/import`,
        {
          method: "POST",
          notifySuccess: "Reviewed directory data imported successfully.",
        },
      );
      await Promise.all([loadBatches(), loadReviewData()]);
    } finally {
      setIsImporting(false);
    }
  }

  async function cancelBatch(batchId: number) {
    if (!window.confirm("Cancel this import batch? It will be put on hold and can be reinstated later."))
      return;

    await api(`/api/community-directory-import/batches/${batchId}/cancel`, {
      method: "POST",
      notifySuccess: "Batch cancelled and placed on hold.",
    });
    await loadBatches();
  }

  async function reinstateBatch(batchId: number) {
    if (!window.confirm("Reinstate this batch so it can be reviewed again?"))
      return;

    await api(`/api/community-directory-import/batches/${batchId}/reinstate`, {
      method: "POST",
      notifySuccess: "Batch reinstated and restored to review.",
    });
    await loadBatches();
  }

  async function importRecord(record: Organisation) {
    const id = record.organisationStagingId;
    const label = record.legalName || record.organisationName;
    const recordKey = `organisation-${id}`;
    if (
      importingRecordKey ||
      !window.confirm(
        `Import "${label}" and all of its included services now? This creates or links the live records.`,
      )
    )
      return;
    setImportingRecordKey(recordKey);
    try {
      await api<ImportResult>(
        `/api/community-directory-import/organisations/${id}/import`,
        {
          method: "POST",
          notifySuccess: "Organisation and services imported successfully.",
        },
      );
      await Promise.all([loadBatches(), loadReviewData()]);
    } finally {
      setImportingRecordKey(null);
    }
  }

  async function changeStatus(
    record: ReviewRecord,
    action: "ready" | "restore" | "reject",
    showSuccessMessage = true,
  ) {
    const section = isScheduleRecord(record)
      ? "schedules"
      : isServiceRecord(record)
        ? "services"
        : "organisations";
    const id = isScheduleRecord(record)
      ? record.serviceScheduleStagingId
      : isServiceRecord(record)
        ? record.serviceStagingId
        : record.organisationStagingId;
    const notifySuccess =
      action === "ready"
        ? "Details confirmed."
        : action === "restore"
          ? "Record restored to review."
          : "Excluded from this import.";
    await api(`/api/community-directory-import/${section}/${id}/${action}`, {
      method: "POST",
      ...(showSuccessMessage ? { notifySuccess } : {}),
    });
    setSelected(null);
    await loadReviewData();
  }

  function readyBlockedReason(record: ReviewRecord) {
    if (isServiceRecord(record)) {
      const organisation = organisations.find(
        (item) => item.organisationStagingId === record.organisationStagingId,
      );
      const organisationIsLive =
        Boolean(
          organisation?.importedPartyId && organisation?.importedTenantId,
        ) ||
        Boolean(organisation?.matchedPartyId && organisation?.matchedTenantId);
      if (!organisationIsLive && organisation?.reviewStatus !== "ReadyToImport")
        return "Mark the organisation ready before marking this service ready.";
    }
    if (isScheduleRecord(record)) {
      const service = services.find(
        (item) => item.serviceStagingId === record.serviceStagingId,
      );
      const serviceIsLive = Boolean(
        service?.importedServiceId || service?.matchedServiceId,
      );
      if (!serviceIsLive && service?.reviewStatus !== "ReadyToImport")
        return "Mark the parent service ready before marking this schedule ready.";
    }
    return undefined;
  }

  return (
    <div className="community-import-page">
      <PageHeader
        title="Community Directory Import"
        subtitle="Validate, review and import organisations, services and recurring schedules."
        actions={headerActions}
      />

      <nav
        className="community-import-tabs"
        aria-label="Directory import sections"
      >
        {tabs.map((item) => (
          <NavLink
            key={item.key}
            to={`/admin/community-directory-import/${item.key}`}
            className={activeTab === item.key ? "active" : ""}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {activeTab === "upload" && (
        <section className="card community-import-upload">
          <ImportSectionHeader
            title="Upload Spreadsheet"
            description="Upload the completed FrontLine template. Every source row is retained, but only valid rows create staging records. No live records are created."
          />
          <form onSubmit={upload}>
            <label className="form-label" htmlFor="directory-workbook">
              Excel workbook (.xlsx)
            </label>
            <input
              id="directory-workbook"
              className="form-input"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={isUploading}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
            <button
              className="action-button"
              type="submit"
              disabled={!file || isUploading}
            >
              {isUploading ? "Validating…" : "Validate and stage"}
            </button>
          </form>
          {summary && <BatchSummary batch={summary} />}
        </section>
      )}

      {activeTab === "batches" && (
        <section className="card">
          <ImportSectionHeader
            title="Import Batches"
            description="View uploaded workbooks and their staging results."
          />
          <BatchTable
            batches={batches}
            onCancelBatch={cancelBatch}
            onReinstateBatch={reinstateBatch}
          />
        </section>
      )}

      {activeTab === "review" && (
        <ConsolidatedImportReview
          batches={reviewBatches}
          selectedBatchId={selectedBatchId}
          onSelectBatch={setSelectedBatchId}
          organisations={organisations}
          services={services}
          schedules={schedules}
          errors={reviewErrors}
          onReview={setSelected}
          onImport={importBatch}
          isImporting={isImporting}
          onImportRecord={importRecord}
          importingRecordKey={importingRecordKey}
          onExclude={(record) => changeStatus(record, "reject")}
          onRestore={(record) => changeStatus(record, "restore")}
          onConfirmationChange={(record, confirmed) =>
            changeStatus(record, confirmed ? "ready" : "restore", false)
          }
          onReject={async (organisation) => {
            if (
              window.confirm(
                `Exclude ${organisation.legalName || organisation.organisationName} and all of its unimported services and schedules from this import? You can then upload the corrected workbook as a new batch.`,
              )
            )
              await changeStatus(organisation, "reject");
          }}
        />
      )}

      {activeTab === "errors" && (
        <section className="card">
          <ImportSectionHeader
            title="Import Errors"
            description="Review rows that could not be staged and download the validation results."
            actions={
              <>
                <select
                  className="form-input community-import-filter"
                  aria-label="Import batch"
                  value={selectedBatchId}
                  onChange={(event) =>
                    setSelectedBatchId(Number(event.target.value))
                  }
                >
                  {batches.map((batch) => (
                    <option
                      key={batch.importBatchId}
                      value={batch.importBatchId}
                    >
                      #{batch.importBatchId} — {batch.originalFileName}
                    </option>
                  ))}
                </select>
                {selectedBatchId > 0 && (
                  <a
                    className="action-button secondary"
                    href={`${config.API_BASE_URL.replace(/\/+$/, "")}/api/community-directory-import/batches/${selectedBatchId}/errors.csv`}
                  >
                    Download errors
                  </a>
                )}
              </>
            }
          />
          <ErrorTable rows={rows} />
        </section>
      )}

      {selected && (
        <ReviewPanel
          record={selected}
          onClose={() => setSelected(null)}
          onStatus={changeStatus}
          readyBlockedReason={readyBlockedReason(selected)}
          onSaved={async (updated) => {
            if (isScheduleRecord(updated)) {
              setSchedules((current) =>
                current.map((item) =>
                  item.serviceScheduleStagingId ===
                  updated.serviceScheduleStagingId
                    ? updated
                    : item,
                ),
              );
            } else if (isServiceRecord(updated)) {
              setServices((current) =>
                current.map((item) =>
                  item.serviceStagingId === updated.serviceStagingId
                    ? updated
                    : item,
                ),
              );
            } else {
              setOrganisations((current) =>
                current.map((item) =>
                  item.organisationStagingId === updated.organisationStagingId
                    ? updated
                    : item,
                ),
              );
            }
            setSelected(null);
            await loadReviewData();
          }}
        />
      )}
    </div>
  );
}

function BatchSummary({ batch }: { batch: ImportBatch }) {
  return (
    <div className="community-import-summary" role="status">
      <strong>
        Import batch #{batch.importBatchId}: {batch.status}
      </strong>
      <span>{batch.organisationCount} organisations</span>
      <span>{batch.serviceCount} services</span>
      <span>{batch.scheduleCount} schedules</span>
      <span>{batch.invalidRows} invalid rows</span>
      <span>{batch.skippedRows} dependent rows skipped</span>
      <strong>0 live records created</strong>
      <NavLink
        className="action-button"
        to="/admin/community-directory-import/review"
      >
        Review this import
      </NavLink>
    </div>
  );
}

function ImportSectionHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="community-import-section-header">
      <div className="community-import-section-heading">
        <h2 className="card-title">{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {actions && (
        <div className="community-import-section-actions">{actions}</div>
      )}
    </div>
  );
}

function BatchTable({
  batches,
  onCancelBatch,
  onReinstateBatch,
}: {
  batches: ImportBatch[];
  onCancelBatch: (batchId: number) => Promise<void>;
  onReinstateBatch: (batchId: number) => Promise<void>;
}) {
  return (
    <div className="table-container">
      <table
        className="community-import-table"
        aria-label="Community directory import batches"
      >
        <thead>
          <tr>
            <th>Batch</th>
            <th>File</th>
            <th>Created</th>
            <th>Status</th>
            <th>Staged</th>
            <th>Invalid</th>
            <th>Contents</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => (
            <tr key={batch.importBatchId}>
              <td>#{batch.importBatchId}</td>
              <td>{batch.originalFileName}</td>
              <td>{formatDate(batch.createdDate)}</td>
              <td>
                <Status value={batch.status} />
              </td>
              <td>{batch.stagedRows}</td>
              <td>{batch.invalidRows}</td>
              <td>
                {batch.organisationCount} orgs · {batch.serviceCount} services ·{" "}
                {batch.scheduleCount} schedules
              </td>
              <td>
                {batch.status === "Cancelled" ? (
                  <button
                    className="action-button secondary"
                    type="button"
                    onClick={() => onReinstateBatch(batch.importBatchId)}
                  >
                    Reinstate
                  </button>
                ) : batch.status !== "Completed" ? (
                  <button
                    className="action-button secondary"
                    type="button"
                    onClick={() => onCancelBatch(batch.importBatchId)}
                  >
                    Cancel
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
          {!batches.length && <Empty colSpan={8} />}
        </tbody>
      </table>
    </div>
  );
}

function ConsolidatedImportReview({
  batches,
  selectedBatchId,
  onSelectBatch,
  organisations,
  services,
  schedules,
  errors,
  onReview,
  onImport,
  isImporting,
  onImportRecord,
  importingRecordKey,
  onExclude,
  onRestore,
  onConfirmationChange,
  onReject,
}: {
  batches: ImportBatch[];
  selectedBatchId: number;
  onSelectBatch: (batchId: number) => void;
  organisations: Organisation[];
  services: Service[];
  schedules: Schedule[];
  errors: ImportRow[];
  onReview: (value: ReviewRecord) => void;
  onImport: (batchId: number) => Promise<void>;
  isImporting: boolean;
  onImportRecord: (value: Organisation) => Promise<void>;
  importingRecordKey: string | null;
  onExclude: (value: ReviewRecord) => Promise<void>;
  onRestore: (value: ReviewRecord) => Promise<void>;
  onConfirmationChange: (
    value: ReviewRecord,
    confirmed: boolean,
  ) => Promise<void>;
  onReject: (organisation: Organisation) => Promise<void>;
}) {
  const { data: staticData } = useStaticData();
  const [rejectingOrganisationId, setRejectingOrganisationId] = useState<
    number | null
  >(null);
  const [activeOrganisationIndex, setActiveOrganisationIndex] = useState(0);
  const [excludingRecordKey, setExcludingRecordKey] = useState<string | null>(
    null,
  );
  const [restoringRecordKey, setRestoringRecordKey] = useState<string | null>(
    null,
  );
  const [confirmingRecordKey, setConfirmingRecordKey] = useState<string | null>(
    null,
  );
  useEffect(() => {
    setActiveOrganisationIndex(0);
  }, [selectedBatchId]);
  const selectedBatch = batches.find(
    (batch) => batch.importBatchId === selectedBatchId,
  );
  const batchOrganisations = organisations.filter(
    (organisation) => organisation.importBatchId === selectedBatchId,
  );
  const organisationIds = new Set(
    batchOrganisations.map(
      (organisation) => organisation.organisationStagingId,
    ),
  );
  const batchServices = services.filter((service) =>
    organisationIds.has(service.organisationStagingId),
  );
  const serviceIds = new Set(
    batchServices.map((service) => service.serviceStagingId),
  );
  const batchSchedules = schedules.filter((schedule) =>
    serviceIds.has(schedule.serviceStagingId),
  );
  const currentOrganisationIndex = Math.min(
    activeOrganisationIndex,
    Math.max(batchOrganisations.length - 1, 0),
  );
  const activeOrganisation =
    batchOrganisations[currentOrganisationIndex] ?? null;
  const goToOrganisation = (index: number) => {
    setActiveOrganisationIndex(
      Math.max(0, Math.min(index, batchOrganisations.length - 1)),
    );
  };
  const allRecords: ReviewRecord[] = [
    ...batchOrganisations,
    ...batchServices,
    ...batchSchedules,
  ];
  const reviewedCount = allRecords.filter(
    (record) => record.reviewStatus !== "Unreviewed",
  ).length;
  const reviewComplete =
    allRecords.length > 0 &&
    allRecords.every((record) =>
      ["ReadyToImport", "Rejected"].includes(record.reviewStatus),
    );
  const serviceCategoryName = (service: Service) =>
    staticData?.serviceCategories?.find(
      (item) => item.serviceCategoryId === service.proposedServiceCategoryId,
    )?.name ??
    service.serviceCategoryName ??
    "Not selected";
  const scheduleReviewSummary = (schedules: Schedule[]) => {
    if (!schedules.length)
      return { value: "Unreviewed", label: "Awaiting review" };
    if (
      schedules.every((schedule) => schedule.reviewStatus === "ReadyToImport")
    )
      return { value: "ReadyToImport", label: "All schedules confirmed" };
    if (schedules.some((schedule) => schedule.reviewStatus === "Rejected"))
      return { value: "Rejected", label: "Some schedules excluded" };
    if (schedules.some((schedule) => schedule.reviewStatus === "NeedsChanges"))
      return { value: "NeedsChanges", label: "Changes need approval" };
    if (schedules.some((schedule) => schedule.reviewStatus === "Unreviewed"))
      return { value: "Unreviewed", label: "Awaiting review" };
    return { value: "ReadyToImport", label: "Schedules confirmed" };
  };

  async function toggleConfirmation(
    record: ReviewRecord,
    confirmed: boolean,
  ) {
    const recordKey = isScheduleRecord(record)
      ? `schedule-${record.serviceScheduleStagingId}`
      : isServiceRecord(record)
        ? `service-${record.serviceStagingId}`
        : `organisation-${record.organisationStagingId}`;
    if (confirmingRecordKey !== null) return;
    setConfirmingRecordKey(recordKey);
    try {
      await onConfirmationChange(record, confirmed);
    } finally {
      setConfirmingRecordKey(null);
    }
  }

  async function rejectOrganisation(organisation: Organisation) {
    if (rejectingOrganisationId !== null) return;
    setRejectingOrganisationId(organisation.organisationStagingId);
    try {
      await onReject(organisation);
    } finally {
      setRejectingOrganisationId(null);
    }
  }

  async function excludeRecord(record: Service | Schedule) {
    const recordKey = isScheduleRecord(record)
      ? `schedule-${record.serviceScheduleStagingId}`
      : `service-${record.serviceStagingId}`;
    const label = isScheduleRecord(record)
      ? record.rawScheduleText
      : record.serviceName;
    if (
      excludingRecordKey !== null ||
      !window.confirm(`Exclude "${label}" from this import?`)
    )
      return;
    setExcludingRecordKey(recordKey);
    try {
      await onExclude(record);
    } finally {
      setExcludingRecordKey(null);
    }
  }

  async function restoreRecord(record: ReviewRecord) {
    const recordKey = isScheduleRecord(record)
      ? `schedule-${record.serviceScheduleStagingId}`
      : isServiceRecord(record)
        ? `service-${record.serviceStagingId}`
        : `organisation-${record.organisationStagingId}`;
    const label = isScheduleRecord(record)
      ? record.rawScheduleText
      : isServiceRecord(record)
        ? record.serviceName
        : record.legalName || record.organisationName;
    if (
      restoringRecordKey !== null ||
      !window.confirm(`Restore "${label}" to this import?`)
    )
      return;
    setRestoringRecordKey(recordKey);
    try {
      await onRestore(record);
    } finally {
      setRestoringRecordKey(null);
    }
  }

  return (
    <section className="community-import-consolidated">
      <div className="card community-import-review-toolbar">
        <div className="community-import-section-header community-import-batch-selector-row">
          <div className="community-import-batch-selector">
            <label className="form-label community-import-batch-label">
              Select Batch
            </label>
            <select
              aria-label="Select batch"
              className="form-input community-import-filter"
              value={selectedBatchId || ""}
              onChange={(event) => onSelectBatch(Number(event.target.value))}
            >
              {!batches.length && <option value="">No import batches</option>}
              {batches.map((batch) => (
                <option key={batch.importBatchId} value={batch.importBatchId}>
                  #{batch.importBatchId} — {batch.originalFileName}
                </option>
              ))}
            </select>
          </div>

          {selectedBatch && (
            <div className="community-import-review-progress community-import-review-progress-inline">
              <div>
                <span>Batch status</span>
                <Status value={selectedBatch.status} />
              </div>
              <div>
                <span>Organisations</span>
                <strong>{batchOrganisations.length}</strong>
              </div>
              <div>
                <span>Services</span>
                <strong>{batchServices.length}</strong>
              </div>
              <div>
                <span>Schedules</span>
                <strong>{batchSchedules.length}</strong>
              </div>
              <div>
                <span>Reviewed</span>
                <strong>
                  {reviewedCount}/{allRecords.length}
                </strong>
              </div>
              <div>
                <span>Validation issues</span>
                <strong>{selectedBatch.invalidRows}</strong>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeOrganisation && (
        <nav
          className="card community-import-organisation-navigator"
          aria-label="Organisation review navigation"
        >
          <div className="community-import-org-control">
            <button
              className="action-button secondary community-import-nav-button"
              type="button"
              disabled={currentOrganisationIndex === 0}
              onClick={() => goToOrganisation(currentOrganisationIndex - 1)}
              aria-label="Previous organisation"
            >
              ‹
            </button>
            <div className="community-import-org-select-wrap">
              <span className="community-import-org-counter">
                {currentOrganisationIndex + 1}/{batchOrganisations.length}
              </span>
              <label className="form-label community-import-org-selector-label">
                <select
                  className="form-input community-import-org-select"
                  value={activeOrganisation.organisationStagingId}
                  onChange={(event) =>
                    goToOrganisation(
                      batchOrganisations.findIndex(
                        (organisation) =>
                          organisation.organisationStagingId ===
                          Number(event.target.value),
                      ),
                    )
                  }
                >
                  {batchOrganisations.map((organisation, index) => (
                    <option
                      key={organisation.organisationStagingId}
                      value={organisation.organisationStagingId}
                    >
                      {index + 1}.{" "}
                      {organisation.legalName || organisation.organisationName}{" "}
                      —{" "}
                      {organisation.importStatus === "Imported"
                        ? statusLabels.Imported
                        : reviewStatusLabel(organisation)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button
              className="action-button community-import-nav-button"
              type="button"
              disabled={
                currentOrganisationIndex === batchOrganisations.length - 1
              }
              onClick={() => goToOrganisation(currentOrganisationIndex + 1)}
              aria-label="Next organisation"
            >
              ›
            </button>
          </div>
        </nav>
      )}

      {activeOrganisation &&
        (() => {
          const organisation = activeOrganisation;
          const organisationServices = batchServices.filter(
            (service) =>
              service.organisationStagingId ===
              organisation.organisationStagingId,
          );
          const organisationReviewComplete =
            organisation.reviewStatus === "ReadyToImport" &&
            organisationServices.every((service) => {
              if (service.reviewStatus === "Rejected") return true;
              if (service.reviewStatus !== "ReadyToImport") return false;
              return batchSchedules
                .filter(
                  (schedule) =>
                    schedule.serviceStagingId === service.serviceStagingId,
                )
                .every((schedule) =>
                  ["ReadyToImport", "Rejected"].includes(schedule.reviewStatus),
                );
            });

          return (
            <article
              className="card community-import-organisation"
              key={organisation.organisationStagingId}
            >
              <header className="community-import-organisation-header">
                <span>
                  <span
                    className="community-import-organisation-title"
                    role="heading"
                    aria-level={3}
                  >
                    {organisation.legalName || organisation.organisationName}
                  </span>
                  <small>
                    {organisationServices.length} service
                    {organisationServices.length === 1 ? "" : "s"}
                  </small>
                  <span
                    className="community-import-confidence-labels"
                    aria-label="Import confidence metadata"
                  >
                    <span className="community-import-confidence-label">
                      <span>Confidence</span>
                      <strong>{organisation.confidence || "Not supplied"}</strong>
                    </span>
                    <span className="community-import-confidence-label community-import-confidence-reason">
                      <span>Reason</span>
                      <strong>
                        {organisation.confidenceReason || "Not supplied"}
                      </strong>
                    </span>
                  </span>
                </span>
                <span className="community-import-summary-statuses">
                  <Status value={organisation.importStatus} />
                  {organisation.importStatus !== "Imported" &&
                    organisation.reviewStatus !== "Rejected" && (
                      <button
                        className="action-button"
                        type="button"
                        disabled={
                          importingRecordKey !== null ||
                          !organisationReviewComplete
                        }
                        title={
                          organisationReviewComplete
                            ? undefined
                            : "Confirm the organisation, every included service and every schedule before importing."
                        }
                        onClick={() => onImportRecord(organisation)}
                      >
                        {importingRecordKey ===
                        `organisation-${organisation.organisationStagingId}`
                          ? "Importing organisation and services…"
                          : "Import Organisation and Services"}
                      </button>
                    )}
                </span>
              </header>

              <div className="community-import-organisation-body">
                <div className="community-import-record-heading">
                  <div className="community-import-section-title-row">
                    <h4 className="card-form-section-title">
                      Organisation Details
                    </h4>
                    <Status
                      value={organisation.reviewStatus}
                      label={reviewStatusLabel(organisation)}
                    />
                  </div>
                  <div className="community-import-record-actions">
                    {organisation.importStatus !== "Imported" &&
                      organisation.reviewStatus !== "Rejected" && (
                        <>
                          <ConfirmationCheckbox
                            checked={
                              organisation.reviewStatus === "ReadyToImport"
                            }
                            disabled={confirmingRecordKey !== null}
                            onChange={(confirmed) =>
                              toggleConfirmation(organisation, confirmed)
                            }
                          />
                          <button
                            className="action-button secondary"
                            type="button"
                            onClick={() => onReview(organisation)}
                          >
                            Edit organisation details
                          </button>
                        </>
                      )}
                    {organisation.reviewStatus === "Rejected" &&
                      organisation.importStatus !== "Imported" && (
                        <button
                          className="action-button secondary"
                          type="button"
                          disabled={restoringRecordKey !== null}
                          onClick={() => restoreRecord(organisation)}
                        >
                          {restoringRecordKey ===
                          `organisation-${organisation.organisationStagingId}`
                            ? "Restoring…"
                            : "Restore to import"}
                        </button>
                      )}
                    {organisation.reviewStatus !== "Rejected" &&
                      organisation.importStatus !== "Imported" && (
                        <button
                          className="action-button danger"
                          type="button"
                          disabled={rejectingOrganisationId !== null}
                          onClick={() => rejectOrganisation(organisation)}
                        >
                          {rejectingOrganisationId ===
                          organisation.organisationStagingId
                            ? "Rejecting…"
                            : "Exclude from this import"}
                        </button>
                      )}
                  </div>
                </div>

                <div className="community-import-detail-grid">
                  <Detail
                    label="Legal name"
                    value={
                      organisation.legalName || organisation.organisationName
                    }
                  />
                  <Detail
                    label="Description"
                    value={organisation.description || "Not supplied"}
                  />
                  <Detail
                    label="Registration number"
                    value={
                      [organisation.charityNumber, organisation.companyNumber]
                        .filter(Boolean)
                        .join(" / ") || "Not supplied"
                    }
                  />
                  <Detail
                    label="Contacts"
                    value={
                      organisation.contacts
                        .map(
                          (contact) =>
                            `${contact.contactType}: ${contact.contactValue}`,
                        )
                        .join(" · ") || "Not supplied"
                    }
                  />
                  <Detail
                    label="Addresses"
                    value={
                      organisation.addresses
                        .map(
                          (address) =>
                            `${address.addressName}, ${address.postcode}`,
                        )
                        .join(" · ") || "Not supplied"
                    }
                  />
                </div>

                <div className="community-import-service-list">
                  <h4 className="card-form-section-title">
                    Services and Schedules
                  </h4>
                  {organisationServices.map((service) => {
                    const serviceSchedules = batchSchedules.filter(
                      (schedule) =>
                        schedule.serviceStagingId === service.serviceStagingId,
                    );
                    const invalidSchedules = errors.filter(
                      (row) =>
                        row.entityType === "Schedule" &&
                        importRowValue(row, "ServiceKey") ===
                          service.serviceKey,
                    );

                    return (
                      <article
                        className="community-import-service"
                        key={service.serviceStagingId}
                      >
                        <div className="community-import-record-heading">
                          <div className="community-import-section-title-row">
                            <h5>{service.serviceName}</h5>
                            <Status
                              value={service.reviewStatus}
                              label={reviewStatusLabel(service)}
                            />
                          </div>
                          <div className="community-import-record-actions">
                            {service.importStatus !== "Imported" &&
                              service.reviewStatus !== "Rejected" && (
                                <>
                                  <ConfirmationCheckbox
                                    checked={
                                      service.reviewStatus === "ReadyToImport"
                                    }
                                    disabled={confirmingRecordKey !== null}
                                    onChange={(confirmed) =>
                                      toggleConfirmation(service, confirmed)
                                    }
                                  />
                                  <button
                                    className="action-button secondary"
                                    type="button"
                                    disabled={
                                      service.importStatus === "Imported"
                                    }
                                    onClick={() => onReview(service)}
                                  >
                                    Edit service details
                                  </button>
                                </>
                              )}
                            {service.reviewStatus === "Rejected" &&
                              service.importStatus !== "Imported" && (
                                <button
                                  className="action-button secondary"
                                  type="button"
                                  disabled={restoringRecordKey !== null}
                                  onClick={() => restoreRecord(service)}
                                >
                                  {restoringRecordKey ===
                                  `service-${service.serviceStagingId}`
                                    ? "Restoring…"
                                    : "Restore to import"}
                                </button>
                              )}
                            {service.importStatus !== "Imported" &&
                              service.reviewStatus !== "Rejected" && (
                                <button
                                  className="action-button danger"
                                  type="button"
                                  disabled={excludingRecordKey !== null}
                                  onClick={() => excludeRecord(service)}
                                >
                                  {excludingRecordKey ===
                                  `service-${service.serviceStagingId}`
                                    ? "Excluding…"
                                    : "Exclude from import"}
                                </button>
                              )}
                          </div>
                        </div>

                        <div className="community-import-detail-grid service-details">
                          <Detail
                            label="Main category"
                            value={serviceCategoryName(service)}
                            attention={!service.proposedServiceCategoryId}
                          />
                          <Detail
                            label="Location"
                            value={service.addressName || "Not supplied"}
                          />
                          <Detail
                            label="Audience"
                            value={service.audience || "Not supplied"}
                          />
                          <Detail
                            label="Access"
                            value={
                              [
                                service.bookingRequired && "Booking required",
                                service.referralRequired && "Referral required",
                                service.publicRegistrationAvailable &&
                                  "Public registration",
                              ]
                                .filter(Boolean)
                                .join(" · ") || "Drop-in or unspecified"
                            }
                          />
                          <Detail
                            label="Cost"
                            value={service.costDescription || "Not supplied"}
                          />
                        </div>

                        <div className="community-import-schedules">
                          <div className="community-import-schedule-heading">
                            <strong>
                              Schedules ({serviceSchedules.length})
                            </strong>
                            {serviceSchedules.length > 0 && (
                              <Status
                                value={
                                  scheduleReviewSummary(serviceSchedules).value
                                }
                                label={
                                  scheduleReviewSummary(serviceSchedules).label
                                }
                              />
                            )}
                          </div>
                          {serviceSchedules.map((schedule) => (
                            <div
                              className="community-import-schedule"
                              key={schedule.serviceScheduleStagingId}
                            >
                              <div>
                                <strong>{schedule.rawScheduleText}</strong>
                                <small>
                                  {formatStructuredSchedule(schedule)}
                                </small>
                              </div>
                              <Status
                                value={schedule.reviewStatus}
                                label={reviewStatusLabel(schedule)}
                              />
                              {schedule.importStatus !== "Imported" &&
                                schedule.reviewStatus !== "Rejected" && (
                                  <>
                                    <ConfirmationCheckbox
                                      checked={
                                        schedule.reviewStatus ===
                                        "ReadyToImport"
                                      }
                                      disabled={confirmingRecordKey !== null}
                                      onChange={(confirmed) =>
                                        toggleConfirmation(schedule, confirmed)
                                      }
                                    />
                                    <button
                                      className="action-button secondary"
                                      type="button"
                                      onClick={() => onReview(schedule)}
                                    >
                                      Edit schedule details
                                    </button>
                                  </>
                                )}
                              {schedule.reviewStatus === "Rejected" &&
                                schedule.importStatus !== "Imported" && (
                                  <button
                                    className="action-button secondary"
                                    type="button"
                                    disabled={restoringRecordKey !== null}
                                    onClick={() => restoreRecord(schedule)}
                                  >
                                    {restoringRecordKey ===
                                    `schedule-${schedule.serviceScheduleStagingId}`
                                      ? "Restoring…"
                                      : "Restore to import"}
                                  </button>
                                )}
                              {schedule.importStatus !== "Imported" &&
                                schedule.reviewStatus !== "Rejected" && (
                                  <button
                                    className="action-button danger"
                                    type="button"
                                    disabled={excludingRecordKey !== null}
                                    onClick={() => excludeRecord(schedule)}
                                  >
                                    {excludingRecordKey ===
                                    `schedule-${schedule.serviceScheduleStagingId}`
                                      ? "Excluding…"
                                      : "Exclude from import"}
                                  </button>
                                )}
                            </div>
                          ))}
                          {invalidSchedules.map((row) => (
                            <div
                              className="community-import-schedule-error"
                              key={row.importBatchRowId}
                            >
                              <div>
                                <strong>
                                  {importRowValue(row, "RawScheduleText") ||
                                    `Schedule row ${row.rowNumber}`}
                                </strong>
                                <small>
                                  This schedule was not staged, so it cannot yet
                                  be edited here.
                                </small>
                                {row.validationErrors.map((error) => (
                                  <span key={error}>{error}</span>
                                ))}
                              </div>
                              <NavLink
                                className="action-button secondary"
                                to="/admin/community-directory-import/errors"
                              >
                                View import error
                              </NavLink>
                            </div>
                          ))}
                          {!serviceSchedules.length &&
                            !invalidSchedules.length && (
                              <p className="community-import-inline-empty">
                                No schedules were imported for this service.
                              </p>
                            )}
                        </div>
                      </article>
                    );
                  })}
                  {!organisationServices.length && (
                    <p className="community-import-inline-empty">
                      No valid services were staged for this organisation.
                    </p>
                  )}
                </div>
              </div>
              {batchOrganisations.length > 1 && (
                <nav
                  className="community-import-organisation-footer"
                  aria-label="Organisation review navigation"
                >
                  <button
                    className="action-button secondary community-import-nav-button"
                    type="button"
                    disabled={currentOrganisationIndex === 0}
                    onClick={() =>
                      goToOrganisation(currentOrganisationIndex - 1)
                    }
                    aria-label="Previous organisation"
                  >
                    ‹
                  </button>
                  <span className="community-import-org-counter-inline">
                    {currentOrganisationIndex + 1}/{batchOrganisations.length}
                  </span>
                  <button
                    className="action-button community-import-nav-button"
                    type="button"
                    disabled={
                      currentOrganisationIndex === batchOrganisations.length - 1
                    }
                    onClick={() =>
                      goToOrganisation(currentOrganisationIndex + 1)
                    }
                    aria-label="Next organisation"
                  >
                    ›
                  </button>
                </nav>
              )}
            </article>
          );
        })()}

      {selectedBatchId > 0 && !batchOrganisations.length && (
        <div className="card community-import-empty">
          No staged organisations were found for this batch. Check Import Errors
          for invalid or skipped rows.
        </div>
      )}
    </section>
  );
}

function Detail({
  label,
  value,
  attention = false,
}: {
  label: string;
  value: string;
  attention?: boolean;
}) {
  return (
    <div className={attention ? "needs-attention" : ""}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ConfirmationCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      className={`action-button secondary community-import-checkbox ${
        checked ? "is-confirmed" : ""
      }`}
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="community-import-checkbox-indicator" aria-hidden="true" />
      {checked ? "Confirmed" : "Confirm"}
    </button>
  );
}

function formatStructuredSchedule(schedule: Schedule) {
  const days = [
    "",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const values = [
    schedule.frequencyType,
    schedule.dayOfWeek ? days[schedule.dayOfWeek] : undefined,
    schedule.startTime?.slice(0, 5),
    schedule.endTime ? `to ${schedule.endTime.slice(0, 5)}` : undefined,
    schedule.termTimeOnly ? "term time only" : undefined,
  ].filter(Boolean);
  return values.length ? values.join(" · ") : "Requires schedule review";
}

function importRowValue(row: ImportRow, key: string): string {
  try {
    const values = JSON.parse(row.rawDataJson) as Record<string, unknown>;
    const value = values[key];
    return typeof value === "string" ? value : "";
  } catch {
    return "";
  }
}

function ErrorTable({ rows }: { rows: ImportRow[] }) {
  return (
    <div className="table-container">
      <table
        className="community-import-table"
        aria-label="Community directory import errors"
      >
        <thead>
          <tr>
            <th>Sheet / row</th>
            <th>Entity</th>
            <th>Key</th>
            <th>Status</th>
            <th>Validation messages</th>
            <th>Original values</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.importBatchRowId}>
              <td>
                {row.sheetName} · {row.rowNumber}
              </td>
              <td>{row.entityType}</td>
              <td>{row.externalKey ?? "—"}</td>
              <td>
                <Status value={row.validationStatus} />
              </td>
              <td>
                {row.validationErrors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </td>
              <td>
                <details>
                  <summary>View row</summary>
                  <pre>
                    {JSON.stringify(JSON.parse(row.rawDataJson), null, 2)}
                  </pre>
                </details>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <Empty colSpan={6} label="No validation errors in this batch." />
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReviewPanel({
  record,
  onClose,
  onStatus,
  readyBlockedReason,
  onSaved,
}: {
  record: ReviewRecord;
  onClose: () => void;
  onStatus: (record: ReviewRecord, action: "ready" | "reject") => Promise<void>;
  readyBlockedReason?: string;
  onSaved: (record: ReviewRecord) => Promise<void>;
}) {
  const { data: staticData, refresh: refreshStaticData } = useStaticData();
  const isSchedule = isScheduleRecord(record);
  const isService = isServiceRecord(record);
  const isOrganisation = !isSchedule && !isService;
  const [form, setForm] = useState<any>({ ...record });
  const [originalRow, setOriginalRow] = useState<ImportRow | null>(null);
  const [busyAction, setBusyAction] = useState<
    "save" | "match" | "ready" | "reject" | "original" | null
  >(null);
  const panelRef = useRef<HTMLElement>(null);
  const attemptedStaticDataRefresh = useRef(false);
  const section = isOrganisation
    ? "organisations"
    : isService
      ? "services"
      : "schedules";
  const id = isScheduleRecord(record)
    ? record.serviceScheduleStagingId
    : isServiceRecord(record)
      ? record.serviceStagingId
      : record.organisationStagingId;
  const title = isScheduleRecord(record)
    ? record.rawScheduleText
    : isServiceRecord(record)
      ? record.serviceName
      : record.legalName || record.organisationName;
  const importBatchRowId = record.importBatchRowId;
  const dialogTitleId = `community-import-review-title-${section}-${id}`;
  const isBusy = busyAction !== null;

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    if (
      !isService ||
      !staticData ||
      attemptedStaticDataRefresh.current ||
      (staticData.serviceCategories?.length ?? 0) > 0
    )
      return;

    attemptedStaticDataRefresh.current = true;
    void refreshStaticData();
  }, [isService, refreshStaticData, staticData]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isBusy) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isBusy, onClose]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (isBusy) return;
    const body = isOrganisation
      ? {
          legalName: form.legalName,
          description: form.description,
          charityNumber: form.charityNumber,
          companyNumber: form.companyNumber,
          website: form.website,
          sourceUrl: form.sourceUrl,
          reviewNotes: form.reviewNotes,
          addresses: form.addresses,
          contacts: form.contacts.map((contact: any) => ({
            organisationContactStagingId:
              contact.organisationContactStagingId,
            contactType: contact.contactType,
            contactValue: contact.contactValue,
            contactLabel: contact.contactLabel,
            isPrimary: contact.isPrimary,
            isPublic: contact.isPublic,
          })),
        }
      : isService
        ? {
            serviceName: form.serviceName,
            description: form.description,
            proposedServiceCategoryId: numberOrNull(
              form.proposedServiceCategoryId,
            ),
            selectedPartyAddressId: numberOrNull(form.selectedPartyAddressId),
            reviewNotes: form.reviewNotes,
          }
        : {
            rawScheduleText: form.rawScheduleText,
            dayOfWeek: numberOrNull(form.dayOfWeek),
            startTime: form.startTime || null,
            endTime: form.endTime || null,
            frequencyType: form.frequencyType || null,
            intervalValue: numberOrNull(form.intervalValue),
            weekOfMonth: numberOrNull(form.weekOfMonth),
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            termTimeOnly: Boolean(form.termTimeOnly),
            reviewNotes: form.reviewNotes,
          };
    setBusyAction("save");
    try {
      const updated = await api<ReviewRecord>(
        `/api/community-directory-import/${section}/${id}`,
        {
          method: "PUT",
          body,
          notifySuccess: "Staging record updated.",
        },
      );
      await onSaved(updated);
    } finally {
      setBusyAction(null);
    }
  }

  async function matchOrganisation() {
    if (!isOrganisation || isBusy) return;
    setBusyAction("match");
    try {
      await api(
        `/api/community-directory-import/organisations/${record.organisationStagingId}/match`,
        {
          method: "POST",
          body: {
            partyId: numberOrNull(form.matchedPartyId),
            tenantId: numberOrNull(form.matchedTenantId),
          },
          notifySuccess: "Existing organisation and tenant matched.",
        },
      );
      const updated = await api<Organisation>(
        `/api/community-directory-import/organisations/${record.organisationStagingId}`,
      );
      await onSaved(updated);
    } finally {
      setBusyAction(null);
    }
  }

  async function viewOriginalRow() {
    if (isBusy) return;
    setBusyAction("original");
    try {
      setOriginalRow(
        await api<ImportRow>(
          `/api/community-directory-import/rows/${importBatchRowId}`,
        ),
      );
    } finally {
      setBusyAction(null);
    }
  }

  async function changeReviewStatus(action: "ready" | "reject") {
    if (isBusy) return;
    if (
      action === "reject" &&
      !window.confirm(
        `Exclude "${title}" from this import? It will not create a live record.`,
      )
    )
      return;
    setBusyAction(action);
    try {
      await onStatus(record, action);
    } finally {
      setBusyAction(null);
    }
  }

  const stagingAddresses: PartyAddress[] = isOrganisation
    ? form.addresses.map((address: any) => ({
        partyAddressId: address.organisationAddressStagingId,
        partyId: 0,
        isPrimary: Boolean(address.isPrimary),
        validFromUtc: form.createdDate,
        validToUtc: null,
        address: {
          addressId: address.organisationAddressStagingId,
          addressLine1: address.addressLine1 || "",
          town: address.townCity || "",
          county: address.county || "",
          postCode: address.postcode || "",
          fullAddress: [
            address.addressLine1,
            address.addressLine2,
            address.townCity,
            address.county,
            address.postcode,
          ]
            .filter(Boolean)
            .join(", "),
        },
      }))
    : [];

  function updateStagingAddresses(addresses: PartyAddress[]) {
    setForm({
      ...form,
      addresses: addresses.map((address) => {
        const existing = form.addresses.find(
          (item: any) =>
            item.organisationAddressStagingId === address.partyAddressId,
        );
        return {
          ...(existing ?? {
            organisationAddressStagingId: 0,
            addressKey: "",
            addressName: address.address.addressLine1 || "Additional address",
            addressType: "Organisation address",
            addressLine2: "",
            country: "United Kingdom",
            isActivityLocation: true,
            sourceUrl: form.sourceUrl,
          }),
          addressLine1: address.address.addressLine1,
          townCity: address.address.town,
          county: address.address.county,
          postcode: address.address.postCode,
          isPrimary: address.isPrimary,
        };
      }),
    });
  }

  const stagingContacts: ContactMethod[] = isOrganisation
    ? form.contacts.map((contact: any) => {
        const importedType = contact.contactType.toUpperCase();
        const expectedCodes =
          importedType === "TELEPHONE"
            ? ["TELEPHONE", "PHONE", "LANDLINE"]
            : [importedType];
        const contactType =
          staticData?.contactTypes.find(
            (type) =>
              expectedCodes.includes(type.code?.toUpperCase() ?? "") ||
              type.name.toUpperCase() === importedType,
          ) ??
          staticData?.contactTypes.find((type) =>
            type.name.toUpperCase().includes(importedType),
          );
        return {
          contactMethodId: contact.organisationContactStagingId,
          tempId:
            contact.clientTempId ?? contact.organisationContactStagingId,
          partyId: 0,
          contactTypeId: contactType?.contactTypeId ?? 0,
          contactTypeCode: contactType?.code ?? contact.contactType,
          contactValue: contact.contactValue,
          isVerified: true,
        };
      })
    : [];

  function updateStagingContacts(contacts: ContactMethod[]) {
    setForm((current: any) => {
      const updatedContacts = contacts.map((contact) => {
        const existing = current.contacts.find(
          (item: any) =>
            (contact.contactMethodId > 0 &&
              item.organisationContactStagingId ===
                contact.contactMethodId) ||
            (contact.contactMethodId <= 0 &&
              item.clientTempId === contact.tempId),
        );
        const contactType = staticData?.contactTypes.find(
          (type) => type.contactTypeId === contact.contactTypeId,
        );
        const code = (
          contactType?.code ?? contact.contactTypeCode
        ).toUpperCase();
        const stagingType =
          code === "EMAIL"
            ? "Email"
            : ["MOBILE", "PHONE", "TELEPHONE", "LANDLINE"].includes(code)
              ? "Telephone"
              : (contactType?.name ?? existing?.contactType ?? code);
        return {
          ...(existing ?? {
            organisationContactStagingId: 0,
            clientTempId: contact.tempId,
            contactLabel: null,
            isPrimary: false,
            isPublic: true,
          }),
          contactType: stagingType,
          contactValue: contact.contactValue,
        };
      });
      const website = updatedContacts.find(
        (contact: any) =>
          contact.contactType?.toUpperCase() === "WEBSITE",
      );

      return {
        ...current,
        contacts: updatedContacts,
        website: website?.contactValue ?? "",
      };
    });
  }

  return (
    <div className="community-import-review-backdrop" role="presentation">
      <aside
        ref={panelRef}
        className="community-import-review-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        tabIndex={-1}
      >
        <div className="community-import-dialog-header">
          <div>
            <h2 className="card-title" id={dialogTitleId}>
              {title}
            </h2>
            <p className="community-import-dialog-statuses">
              <Status
                value={record.reviewStatus}
                label={reviewStatusLabel(record)}
              />
            </p>
          </div>
          <button
            className="action-button secondary"
            type="button"
            disabled={isBusy}
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <form onSubmit={save}>
          {isOrganisation && (
            <>
              <section
                className="community-import-edit-section"
                aria-labelledby={`${dialogTitleId}-organisation-details`}
              >
                <h3
                  className="card-form-section-title"
                  id={`${dialogTitleId}-organisation-details`}
                >
                  Organisation Details
                </h3>
                <Field
                  label="Legal name"
                  value={form.legalName}
                  onChange={(v) => setForm({ ...form, legalName: v })}
                />
                <label className="form-label">
                  Description
                  <textarea
                    className="form-input"
                    value={form.description || ""}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </label>
                <Field
                  label="Charity number"
                  value={form.charityNumber}
                  onChange={(v) => setForm({ ...form, charityNumber: v })}
                />
                <Field
                  label="Company number"
                  value={form.companyNumber}
                  onChange={(v) => setForm({ ...form, companyNumber: v })}
                />
                <Field
                  label="Source URL"
                  value={form.sourceUrl}
                  onChange={(v) => setForm({ ...form, sourceUrl: v })}
                  linkUrl={form.sourceUrl}
                />
                <div className="community-import-reused-card">
                  <AddressCard
                    partyId={0}
                    addresses={stagingAddresses}
                    onChange={updateStagingAddresses}
                    allowAddAnother
                    allowDelete={false}
                  />
                </div>
                <div className="community-import-reused-card">
                  <ContactCard
                    partyId={0}
                    contacts={stagingContacts}
                    onChange={updateStagingContacts}
                    contactPreferences={[]}
                    onPreferenceChange={() => undefined}
                    allowAddAnother
                    allowDelete
                    allowDeleteDefaultContacts
                    allowTypeChange
                    ensureDefaultContacts={false}
                    emptyMessage="No contact details were included in the spreadsheet."
                  />
                </div>
                <div className="community-import-match-fields">
                  <h4 className="card-form-section-title">
                    Existing Organisation Match
                  </h4>
                  <Field
                    label="Matched Party ID"
                    type="number"
                    value={form.matchedPartyId}
                    onChange={(v) => setForm({ ...form, matchedPartyId: v })}
                  />
                  <Field
                    label="Matched Tenant ID"
                    type="number"
                    value={form.matchedTenantId}
                    onChange={(v) => setForm({ ...form, matchedTenantId: v })}
                  />
                  <button
                    className="action-button secondary"
                    type="button"
                    onClick={matchOrganisation}
                    disabled={
                      isBusy || !form.matchedPartyId || !form.matchedTenantId
                    }
                  >
                    {busyAction === "match"
                      ? "Matching…"
                      : "Match existing organisation"}
                  </button>
                </div>
              </section>
            </>
          )}
          {isService && (
            <>
              <section
                className="community-import-edit-section"
                aria-labelledby={`${dialogTitleId}-service-details`}
              >
                <h3
                  className="card-form-section-title"
                  id={`${dialogTitleId}-service-details`}
                >
                  Service Details
                </h3>
                <Field
                  label="Service name"
                  value={form.serviceName}
                  onChange={(v) => setForm({ ...form, serviceName: v })}
                />
                <label className="form-label">
                  Main service category
                  <select
                    className="form-input"
                    value={form.proposedServiceCategoryId ?? ""}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        proposedServiceCategoryId: event.target.value
                          ? Number(event.target.value)
                          : "",
                      })
                    }
                    disabled={!staticData?.serviceCategories?.length}
                  >
                    <option value="">Select main category</option>
                    {(staticData?.serviceCategories ?? []).map((category) => (
                      <option
                        key={category.serviceCategoryId}
                        value={category.serviceCategoryId}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {record.serviceCategoryName && (
                    <span className="field-hint">
                      Imported value: {record.serviceCategoryName}
                    </span>
                  )}
                </label>
                <Field
                  label="Live PartyAddress ID"
                  type="number"
                  value={form.selectedPartyAddressId}
                  onChange={(v) =>
                    setForm({ ...form, selectedPartyAddressId: v })
                  }
                />
                <p>
                  <strong>Audience:</strong> {record.audience || "Not supplied"}
                </p>
                <p>
                  <strong>Booking required:</strong>{" "}
                  {yesNo(record.bookingRequired)} ·{" "}
                  <strong>Referral required:</strong>{" "}
                  {yesNo(record.referralRequired)}
                </p>
              </section>
            </>
          )}
          {!isOrganisation && !isService && (
            <>
              <section
                className="community-import-edit-section"
                aria-labelledby={`${dialogTitleId}-schedule-details`}
              >
                <h3
                  className="card-form-section-title"
                  id={`${dialogTitleId}-schedule-details`}
                >
                  Schedule Details
                </h3>
                <Field
                  label="Raw schedule text"
                  value={form.rawScheduleText}
                  onChange={(v) => setForm({ ...form, rawScheduleText: v })}
                />
                <Field
                  label="Day of week"
                  value={form.dayOfWeek}
                  onChange={(v) => setForm({ ...form, dayOfWeek: v })}
                />
                <Field
                  label="Start time"
                  type="time"
                  value={form.startTime}
                  onChange={(v) => setForm({ ...form, startTime: v })}
                />
                <Field
                  label="End time"
                  type="time"
                  value={form.endTime}
                  onChange={(v) => setForm({ ...form, endTime: v })}
                />
                <label className="form-label">
                  Frequency
                  <select
                    className="form-input"
                    value={form.frequencyType || ""}
                    onChange={(e) =>
                      setForm({ ...form, frequencyType: e.target.value })
                    }
                  >
                    <option value="">Select</option>
                    {[
                      "Daily",
                      "Weekly",
                      "Fortnightly",
                      "Monthly",
                      "TermTime",
                      "Variable",
                    ].map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <Field
                  label="Interval"
                  type="number"
                  value={form.intervalValue}
                  onChange={(v) => setForm({ ...form, intervalValue: v })}
                />
                <Field
                  label="Week of month"
                  type="number"
                  value={form.weekOfMonth}
                  onChange={(v) => setForm({ ...form, weekOfMonth: v })}
                />
                <Field
                  label="Start date"
                  type="date"
                  value={form.startDate}
                  onChange={(v) => setForm({ ...form, startDate: v })}
                />
                <Field
                  label="End date"
                  type="date"
                  value={form.endDate}
                  onChange={(v) => setForm({ ...form, endDate: v })}
                />
                <label className="community-import-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.termTimeOnly)}
                    onChange={(event) =>
                      setForm({ ...form, termTimeOnly: event.target.checked })
                    }
                  />
                  Term-time only
                </label>
              </section>
            </>
          )}
          <section
            className="community-import-edit-section"
            aria-labelledby={`${dialogTitleId}-review-notes`}
          >
            <h3
              className="card-form-section-title"
              id={`${dialogTitleId}-review-notes`}
            >
              Review Notes
            </h3>
            <label className="form-label">
              Review notes
              <textarea
                className="form-input"
                value={form.reviewNotes || ""}
                onChange={(e) =>
                  setForm({ ...form, reviewNotes: e.target.value })
                }
              />
            </label>
            {readyBlockedReason && (
              <div className="community-import-readiness-message" role="status">
                <strong>Complete the parent review first.</strong>
                <span>{readyBlockedReason}</span>
              </div>
            )}
            <div className="community-import-review-actions">
              <button className="action-button" type="submit" disabled={isBusy}>
                {busyAction === "save" ? "Saving…" : "Save corrections"}
              </button>
              {"sourceUrl" in record && record.sourceUrl && (
                <a
                  className="action-button secondary"
                  href={record.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View source
                </a>
              )}
              <button
                className="action-button secondary"
                type="button"
                onClick={viewOriginalRow}
                disabled={isBusy}
              >
                {busyAction === "original"
                  ? "Loading row…"
                  : "View original row"}
              </button>
            </div>
          </section>
          {originalRow && (
            <section
              className="community-import-original-row"
              aria-labelledby={`${dialogTitleId}-original-row`}
            >
              <h3
                className="card-form-section-title"
                id={`${dialogTitleId}-original-row`}
              >
                Original Spreadsheet Row
              </h3>
              <strong>
                {originalRow.sheetName}, row {originalRow.rowNumber}
              </strong>
              {originalRow.validationErrors.map((error) => (
                <div key={error}>{error}</div>
              ))}
              {originalRow.validationWarnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
              <pre>
                {JSON.stringify(JSON.parse(originalRow.rawDataJson), null, 2)}
              </pre>
            </section>
          )}
        </form>
      </aside>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  linkUrl,
}: {
  label: string;
  value?: string | number;
  onChange: (value: string) => void;
  type?: string;
  linkUrl?: string;
}) {
  if (label.startsWith("Day of week")) {
    return (
      <label className="form-label">
        Day of week
        <select
          className="form-input"
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Not specified</option>
          {[
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ].map((day, index) => (
            <option key={day} value={index + 1}>
              {day}
            </option>
          ))}
        </select>
      </label>
    );
  }
  const safeLinkUrl =
    linkUrl && /^https?:\/\//i.test(linkUrl.trim()) ? linkUrl.trim() : undefined;
  return (
    <label className="form-label">
      <span className="community-import-field-label">
        <span>{label}</span>
        {safeLinkUrl && (
          <a
            className="community-import-source-link"
            href={safeLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open source URL in a new tab"
            title="Open source URL in a new tab"
            onClick={(event) => event.stopPropagation()}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 3h7v7" />
              <path d="M10 14 21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          </a>
        )}
      </span>
      <input
        className="form-input"
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
const statusLabels: Record<string, string> = {
  Unreviewed: "Awaiting review",
  NeedsChanges: "Changes need approval",
  ReadyToImport: "Details confirmed",
  Rejected: "Excluded from import",
  Pending: "Not imported yet",
  Imported: "Imported successfully",
  ReadyForReview: "Ready for review",
  Completed: "Import completed",
  Cancelled: "Cancelled",
  ValidationFailed: "Validation failed",
  Staged: "Validated and staged",
  Invalid: "Could not be validated",
  Skipped: "Skipped because a related row has errors",
  Duplicate: "Duplicate row",
};
function reviewStatusLabel(record: ReviewRecord) {
  if (record.reviewStatus !== "ReadyToImport")
    return statusLabels[record.reviewStatus] ?? record.reviewStatus;
  if (isScheduleRecord(record)) return "Schedule details confirmed";
  if (isServiceRecord(record)) return "Service details confirmed";
  return "Organisation details confirmed";
}
function Status({ value, label }: { value: string; label?: string }) {
  return (
    <span className={`community-import-status status-${value.toLowerCase()}`}>
      {label ?? statusLabels[value] ?? value}
    </span>
  );
}
function Empty({
  colSpan,
  label = "No staging records found.",
}: {
  colSpan: number;
  label?: string;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="community-import-empty">
        {label}
      </td>
    </tr>
  );
}
function numberOrNull(value: unknown) {
  return value === "" || value == null ? null : Number(value);
}
